import {
  createWalletClient,
  createPublicClient,
  custom,
  http,
  erc20Abi,
  formatUnits,
  type Address,
} from 'viem';
import { base } from 'viem/chains';
import {
  BASE_CHAIN_ID,
  BASE_CHAIN_ID_HEX,
  USDC_BASE_ADDRESS,
  usdcBaseUnits,
} from '@gilding/shared';
import { ensurePaymentProvider } from './siwe.js';

export type PayStatus =
  | 'connecting'
  | 'switching-network'
  | 'checking-balance'
  | 'awaiting-approval'
  | 'confirming';

export type PayErrorCode =
  | 'no_wallet'
  | 'wrong_network'
  | 'wrong_account'
  | 'insufficient_usdc'
  | 'rejected'
  | 'send_failed';

export class PayError extends Error {
  constructor(
    public readonly code: PayErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'PayError';
  }
}

interface EipProvider {
  request(args: { method: string; params?: unknown[] }): Promise<unknown>;
}

/**
 * Pay for a pack by sending exactly `priceUsd` of USDC on Base to `treasury`.
 *
 * The app builds the whole transfer — correct token, amount, recipient, chain —
 * so the user can't fat-finger any of it; they just approve once. Returns the
 * transaction hash for the worker to verify. Throws PayError with a friendly,
 * code-tagged message on every failure path.
 */
export async function payForPack(opts: {
  priceUsd: number;
  treasury: string;
  from: string;
  onStatus?: (s: PayStatus) => void;
  /** Skip the pre-flight balance check — the wallet is the final authority. */
  allowLowBalance?: boolean;
}): Promise<string> {
  const { priceUsd, treasury, from, onStatus, allowLowBalance } = opts;

  // Being signed in doesn't guarantee a live wallet object (the provider is
  // lost on reload) — re-establish it. On WalletConnect this restores the
  // session, which on mobile may bounce the user to their wallet app to approve.
  onStatus?.('connecting');
  const provider = (await ensurePaymentProvider());
  if (!provider) {
    throw new PayError(
      'no_wallet',
      'Reconnect your wallet to pay. Tap “Try again,” then approve the connection in your wallet.',
    );
  }

  // Guard: pay from the wallet you signed in with. The worker only credits a
  // transfer whose sender matches your session, so a different account would
  // pay real USDC and get no leaves. Catch it here with a clear message.
  try {
    const accounts = (await provider.request({ method: 'eth_accounts' })) as string[];
    if (
      accounts.length > 0 &&
      !accounts.some((a) => a.toLowerCase() === from.toLowerCase())
    ) {
      throw new PayError(
        'wrong_account',
        `Connect the wallet you signed in with (${short(from)}) to pay. Your wallet is on a different account.`,
      );
    }
  } catch (e) {
    if (e instanceof PayError) throw e;
    /* eth_accounts unsupported — let the chain enforce the sender */
  }

  // 1. Make sure the wallet is on Base.
  onStatus?.('switching-network');
  await ensureBase(provider);

  const amount = usdcBaseUnits(priceUsd);
  const account = from as Address;
  const usdc = USDC_BASE_ADDRESS as Address;

  // 2. Confirm they actually hold enough USDC on Base before asking to sign.
  onStatus?.('checking-balance');
  const publicClient = createPublicClient({ chain: base, transport: http() });
  let balance: bigint;
  try {
    balance = await publicClient.readContract({
      address: usdc,
      abi: erc20Abi,
      functionName: 'balanceOf',
      args: [account],
    });
  } catch {
    balance = amount; // RPC hiccup — don't block; the chain enforces it anyway
  }
  if (!allowLowBalance && balance < amount) {
    throw new PayError(
      'insufficient_usdc',
      `We couldn’t find ${priceUsd} USDC on Base in this wallet (it reads ${formatUnits(balance, 6)}). ` +
        `Two common reasons: you’re signed in with a different wallet than the one holding your USDC, ` +
        `or your balance is bridged USDbC rather than native USDC. ` +
        `If you’re sure this wallet has USDC on Base, tap “Pay anyway.” Your wallet shows the final amount before you approve.`,
    );
  }

  // 3. One approval — the exact transfer.
  onStatus?.('awaiting-approval');
  const walletClient = createWalletClient({ chain: base, transport: custom(provider) });
  let hash: `0x${string}`;
  try {
    hash = await walletClient.writeContract({
      account,
      chain: base,
      address: usdc,
      abi: erc20Abi,
      functionName: 'transfer',
      args: [treasury as Address, amount],
    });
  } catch (e) {
    if (isUserRejection(e)) {
      throw new PayError('rejected', 'Payment cancelled in your wallet.');
    }
    throw new PayError('send_failed', describe(e) || 'The payment transaction failed to send.');
  }

  // 4. Wait briefly for it to land (best-effort; the worker re-verifies).
  onStatus?.('confirming');
  try {
    await publicClient.waitForTransactionReceipt({ hash, confirmations: 1, timeout: 90_000 });
  } catch {
    /* may still be mining — the worker confirm step will poll/verify */
  }
  return hash;
}

async function ensureBase(provider: EipProvider): Promise<void> {
  // Already there? Nothing to do.
  if ((await currentChainId(provider)) === BASE_CHAIN_ID) return;

  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: BASE_CHAIN_ID_HEX }],
    });
  } catch (e) {
    const code = (e as { code?: number })?.code;
    if (code === 4902) {
      // Wallet doesn't know Base yet — add it, then switch onto it.
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: BASE_CHAIN_ID_HEX,
            chainName: 'Base',
            nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
            rpcUrls: ['https://mainnet.base.org'],
            blockExplorerUrls: ['https://basescan.org'],
          },
        ],
      });
      await provider
        .request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: BASE_CHAIN_ID_HEX }],
        })
        .catch(() => undefined);
    } else if (isUserRejection(e)) {
      throw new PayError('wrong_network', 'Approve the switch to Base in your wallet, then try again.');
    }
    // other errors fall through to the verification below
  }

  // Verify the wallet actually landed on Base before we build the transaction.
  // Some wallets resolve the switch request before the active chain changes,
  // and others (e.g. a mainnet-only WalletConnect session) silently fail to
  // switch at all — either way, building the tx now would send on the wrong
  // chain, so confirm first and surface a clear, actionable message if not.
  for (let i = 0; i < 12; i++) {
    if ((await currentChainId(provider)) === BASE_CHAIN_ID) return;
    await new Promise((r) => setTimeout(r, 300));
  }
  throw new PayError(
    'wrong_network',
    'Your wallet is still on Ethereum. Switch it to the Base network, then tap Pay again. ' +
      '(If you signed in with a QR/WalletConnect, open this page inside your wallet app’s browser to pay.)',
  );
}

/** The wallet's current chain id as a number (0 if it can't be read). */
async function currentChainId(provider: EipProvider): Promise<number> {
  try {
    const raw = await provider.request({ method: 'eth_chainId' });
    if (typeof raw === 'string') return parseInt(raw, 16);
    if (typeof raw === 'number') return raw;
    return 0;
  } catch {
    return 0;
  }
}

function isUserRejection(e: unknown): boolean {
  const err = e as { code?: number | string; message?: string } | undefined;
  if (!err) return false;
  if (err.code === 4001 || err.code === '4001') return true;
  const m = (err.message ?? '').toLowerCase();
  return m.includes('user reject') || m.includes('user denied') || m.includes('rejected');
}

function describe(e: unknown): string {
  if (e instanceof Error) return e.message.split('\n')[0] ?? e.message;
  return String(e);
}

function short(addr: string): string {
  return addr.length > 12 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr;
}
